import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { t } from '../i18n/t';
import { useTabs } from './TabsContext';
import { resolveIcon } from '../components/AppShell/sidebarIcons';
import { MAX_TABS, type LopTab } from './types';
import './LopTabsCapModal.css';

/**
 * Modal shown when a navigation is refused at the MAX_TABS cap (D5 —
 * reducer.ts CAP_BLOCKED / TabRouterSync.tsx). Mounted ONCE in AppShell
 * (portalled to document.body, always above any tab pane, like
 * LopTabSwitcher). Replaces the old flashed "max tabs" message with a
 * closable list of the currently open tabs — closing one from here frees a
 * slot, at which point אישור continues the originally-blocked navigation as
 * a normal push (opens the new tab through the existing PUSH path).
 *
 * `role="dialog"` here makes `dialogGuard.isDialogOpen()` true while this is
 * open, same as every other app dialog — so its own × handlers call `close`
 * directly from useTabs() rather than LopTabBar's guarded closeWithConfirm
 * (which would no-op, seeing this very dialog as "a dialog is open").
 */
export function LopTabsCapModal() {
  const { tabs, capBlocked, close, dismissCapBlocked } = useTabs();
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const open = capBlocked !== null;

  // Pinned home never appears — always present, never closeable, so it can
  // never free a slot; showing it would just be noise.
  const closableTabs = useMemo(() => tabs.filter((tab) => !tab.pinned), [tabs]);
  const canConfirm = tabs.length < MAX_TABS;

  const handleCloseTab = useCallback(
    (tab: LopTab) => {
      if (tab.dirty && !window.confirm(`${tab.title}: ${t('tabs.unsavedConfirm')}`)) return;
      close(tab.key);
    },
    [close],
  );

  const handleCancel = useCallback(() => {
    dismissCapBlocked();
  }, [dismissCapBlocked]);

  const handleConfirm = useCallback(() => {
    if (!canConfirm || !capBlocked) return;
    const location = capBlocked;
    dismissCapBlocked();
    // A normal push — the same path a fresh sidebar click would take, so it
    // opens through TabRouterSync's ordinary PUSH -> navOpenOrFocus branch.
    navigate(location);
  }, [canConfirm, capBlocked, dismissCapBlocked, navigate]);

  // Esc = ביטול. Tab/Shift+Tab is trapped inside the dialog while open.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleCancel();
        return;
      }
      if (e.key !== 'Tab') return;
      const container = dialogRef.current;
      if (!container) return;
      const focusable = container.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, handleCancel]);

  // Initial focus on ביטול when the modal opens.
  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="lop-tabs-cap-modal__backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleCancel();
      }}
    >
      <div
        className="lop-tabs-cap-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lop-tabs-cap-modal-title"
        ref={dialogRef}
      >
        <header className="lop-tabs-cap-modal__header">
          <h2 id="lop-tabs-cap-modal-title" className="lop-tabs-cap-modal__title">
            {t('tabs.maxTabsReached')}
          </h2>
          <span className="lop-tabs-cap-modal__counter">
            {t('tabs.capModalCounter', { count: tabs.length, max: MAX_TABS })}
          </span>
        </header>

        <p className="lop-tabs-cap-modal__explanation">{t('tabs.capModalExplanation')}</p>

        <ul className="lop-tabs-cap-modal__list">
          {closableTabs.map((tab) => {
            const Icon = resolveIcon(tab.iconName);
            return (
              <li key={tab.key} className="lop-tabs-cap-modal__item">
                {Icon && <Icon size={14} className="lop-tabs-cap-modal__icon" aria-hidden="true" />}
                <span className="lop-tabs-cap-modal__item-title">{tab.title}</span>
                {tab.dirty && <span className="lop-tabs-cap-modal__dirty-dot" aria-hidden="true" />}
                <button
                  type="button"
                  className="lop-tabs-cap-modal__item-close"
                  aria-label={t('tabs.close')}
                  onClick={() => handleCloseTab(tab)}
                >
                  <X size={12} aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>

        <footer className="lop-tabs-cap-modal__footer">
          <button
            type="button"
            className="lop-tabs-cap-modal__btn lop-tabs-cap-modal__btn--primary"
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            {t('tabs.capModalConfirm')}
          </button>
          <button
            type="button"
            ref={cancelRef}
            className="lop-tabs-cap-modal__btn lop-tabs-cap-modal__btn--secondary"
            onClick={handleCancel}
          >
            {t('tabs.capModalCancel')}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
